import { useState, useRef, useEffect, useCallback } from "react";
import { X, Undo2, Eraser, Trash2 } from "lucide-react";

const COLOURING_PAGES = [
  {
    id: "poodle-picnic",
    name: "Poodle Picnic",
    image: "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/s3o76n05_colouring1.jpg",
  },
];

const COLOURS = [
  '#FF6B6B', '#FF9E7D', '#FFCF48', '#7ED957', '#4ECDC4',
  '#45B7D1', '#96C', '#FF69B4', '#C9A96E', '#8B4513',
  '#FFB6C1', '#FFDAB9', '#FFFACD', '#B5EAD7', '#C7CEEA',
  '#D4A5FF', '#FFFFFF', '#333333',
];

const BRUSH_SIZES = [
  { label: 'S', size: 8 },
  { label: 'M', size: 16 },
  { label: 'L', size: 28 },
  { label: 'XL', size: 42 },
];

const ColouringBook = ({ isOpen, onClose }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [selectedColour, setSelectedColour] = useState(COLOURS[0]);
  const [brushSize, setBrushSize] = useState(16);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [history, setHistory] = useState([]);
  const [isEraser, setIsEraser] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const lastPosRef = useRef(null);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || !img.complete) return;

    // Set canvas to image's natural resolution (fixed, won't change on resize)
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    setImageLoaded(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const img = imageRef.current;
    if (img && img.complete) {
      setupCanvas();
    }
  }, [isOpen, currentPage, setupCanvas]);

  const saveToHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const data = canvas.toDataURL();
    setHistory(prev => [...prev.slice(-20), data]);
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    // Scale from display coordinates to canvas coordinates
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (touch.clientX - rect.left) * scaleX,
      y: (touch.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    saveToHistory();
    setIsDrawing(true);
    const pos = getPos(e);
    lastPosRef.current = pos;
    draw(pos);
  };

  const draw = (pos) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scale = canvas.width / rect.width;
    ctx.globalCompositeOperation = isEraser ? 'destination-out' : 'source-over';
    ctx.strokeStyle = selectedColour;
    ctx.lineWidth = brushSize * scale;
    ctx.globalAlpha = isEraser ? 1 : 0.7;

    if (lastPosRef.current) {
      ctx.beginPath();
      ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    lastPosRef.current = pos;
  };

  const moveDraw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    draw(getPos(e));
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const lastState = history[history.length - 1];

    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = lastState;
    setHistory(prev => prev.slice(0, -1));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    saveToHistory();
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  if (!isOpen) return null;

  const page = COLOURING_PAGES[currentPage];

  return (
    <div className="fixed inset-0 z-[80] bg-[#FDFBF7] flex flex-col" data-testid="colouring-book">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-white/90 backdrop-blur-md border-b border-[#E8E5DC]">
        <h2 className="text-base font-bold text-[#4A4A4A]">Colouring Book</h2>
        <button onClick={onClose} data-testid="colouring-close"
          className="p-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] transition-all">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Page selector (for when more pages are added) */}
      {COLOURING_PAGES.length > 1 && (
        <div className="flex justify-center gap-2 py-2 bg-white/50">
          {COLOURING_PAGES.map((p, i) => (
            <button key={p.id} onClick={() => { setCurrentPage(i); setHistory([]); }}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                i === currentPage ? 'bg-[#E8D4A8] text-[#5A4A32]' : 'bg-[#F3F0E6] text-[#6B6B6B]'
              }`}>
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Canvas area */}
      <div className="flex-1 overflow-auto flex flex-col items-center justify-center p-2 sm:p-4">
        {/* Brush size - above image */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-[#9A9A9A] font-medium">Brush size</span>
          {BRUSH_SIZES.map((b) => (
            <button key={b.label} onClick={() => { setBrushSize(b.size); setIsEraser(false); }}
              className={`w-8 h-8 rounded-full text-xs font-bold transition-all flex items-center justify-center ${
                brushSize === b.size && !isEraser ? 'bg-[#E8D4A8] text-[#5A4A32] ring-2 ring-[#B89823]' : 'bg-[#F3F0E6] text-[#6B6B6B]'
              }`}>
              {b.label}
            </button>
          ))}
          <button onClick={() => setIsEraser(!isEraser)}
            className={`p-2 rounded-full transition-all ${isEraser ? 'bg-[#A55B64] text-white' : 'bg-[#F3F0E6] text-[#6B6B6B]'}`}>
            <Eraser className="w-4 h-4" />
          </button>
          <button onClick={handleUndo} disabled={history.length === 0}
            className="p-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] disabled:opacity-40">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={handleClear}
            className="p-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className="relative w-full max-w-5xl">
          <img
            ref={imageRef}
            src={page.image}
            alt={page.name}
            onLoad={setupCanvas}
            className="w-full h-auto max-h-[calc(100vh-140px)] object-contain pointer-events-none relative z-10"
            style={{ mixBlendMode: 'multiply' }}
          />
          <canvas
            ref={canvasRef}
            onMouseDown={startDraw}
            onMouseMove={moveDraw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={moveDraw}
            onTouchEnd={endDraw}
            className="absolute top-0 left-0 w-full h-full z-0 cursor-crosshair"
            style={{ touchAction: 'none' }}
          />
        </div>
      </div>

      {/* Colour palette only at bottom */}
      <div className="bg-white/95 backdrop-blur-md border-t border-[#E8E5DC] px-3 py-2">
        <div className="flex flex-wrap justify-center gap-1.5">
          {COLOURS.map((colour) => (
            <button
              key={colour}
              onClick={() => { setSelectedColour(colour); setIsEraser(false); }}
              className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${
                selectedColour === colour && !isEraser ? 'border-[#4A4A4A] scale-110 ring-2 ring-[#4A4A4A]/30' : 'border-[#E8E5DC]'
              }`}
              style={{ backgroundColor: colour }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ColouringBook;
